/**
 * Explore shell (POS2-880, reworked POS2-1000) — full-height single-canvas analysis
 * view. The topbar carries the granularity + measure toggles; the date range and the
 * Split control live in the Filters popover. Below the panel chrome the engine pivot
 * (POS2-882) renders as one of four views — Line/Bar (POS2-883), Pie/Table (POS2-884).
 * All control state lives in the URL (`useExploreState` + the shared `useWasteFilters`).
 */
import { useMemo, useState, type ReactNode } from "react";
import { Link } from "react-router";
import { Camera, Download } from "lucide-react";
import { toast } from "sonner";

import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { cn } from "@/lib/utils";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { SegmentedToggle } from "@/components/waste/reports/segmented-toggle";
import { type Granularity } from "@/lib/explore-window";
import { type ExploreView, type Measure, useExploreState } from "@/hooks/use-explore-state";
import { transactionsHref } from "@/lib/explore-link";
import { zonedDay } from "@/lib/zoned-day";
import {
  EXPLORE_MEASURES,
  GRAN_UNIT_KEYS,
  exploreTitle,
  shapeExplorePivot,
  type ExplorePivot,
} from "@/lib/explore-engine";
import { exploreCsv, exportFilename } from "@/lib/explore-export";
import { buildChartData, buildPieData, isSingleBucketWindow } from "@/lib/explore-chart";
import { ExploreLine } from "@/components/waste/explore/line-view";
import { ExploreBar } from "@/components/waste/explore/bar-view";
import { ExplorePie } from "@/components/waste/explore/pie-view";
import { ExploreTable } from "@/components/waste/explore/table-view";
import { useExploreData } from "@/hooks/use-explore-data";
import { FilterPopover, type FilterField } from "@/components/waste/filter-popover";
import { useTranslation } from "@/i18n/use-translation";
import { currencySymbol, formatNumber, formatPercent } from "@/lib/format";
import { useWasteFilters, type DatePreset } from "@/hooks/use-waste-filters";
import { useSitesWithActiveMenus, useKitchens } from "@/hooks/use-active-menus";
import { useDevices } from "@/hooks/use-devices";
import { useClaims } from "@/hooks/use-claims";
import { useAdminOrgStore } from "@/stores/admin-org";
import { useResolvedConfig, type EntityType } from "@/hooks/use-resolved-config";
import { pickDefaultSiteId } from "@/lib/sites";
import { serviceTimeLabel } from "@/lib/service-times";
import { wasteTypeLabel } from "@/lib/waste-types";

// Explore's window is driven by the shared date_from/date_to (the popover's date field),
// not a useWasteFilters preset — "all" keeps useWasteFilters from imposing its own date
// window; the granularity default (defaultWindow) applies when no custom range is set.
const EXPLORE_PRESET: DatePreset = "all";
// Explore's filter popover (Artem, POS2-1000): custom date range, time-of-day, site,
// sensor, then the Split control, waste type, service time. Split rides the draft/Apply
// cycle like the other filters (POS2-884). Excluded (row-level, kept on Transactions):
// price, weight, tier, confirmation, kitchen, food. Note service/waste-type are also
// split options — filtering + splitting the same dimension collapses to one series.
const EXPLORE_FILTER_FIELDS: FilterField[] = [
  "dates",
  "times",
  "site",
  "sensor",
  "split",
  "waste_type",
  "service_time",
];

// Literal keys resolved at render — a module-level constant that called `t()` would capture the
// locale at import (POS2-1134 CP3a).
const GRAN_OPTION_KEYS: { value: Granularity; labelKey: string }[] = [
  { value: "daily", labelKey: "waste.granDaily" },
  { value: "weekly", labelKey: "waste.granWeekly" },
  { value: "monthly", labelKey: "waste.granMonthly" },
];
const VIEW_OPTION_KEYS: { value: ExploreView; labelKey: string }[] = [
  { value: "line", labelKey: "waste.viewLine" },
  { value: "bar", labelKey: "waste.viewBar" },
  { value: "pie", labelKey: "waste.viewPie" },
  { value: "table", labelKey: "waste.viewTable" },
];

/**
 * Explore's permanent home (POS2-1045), launched to every user with waste read access
 * (POS2-1046, approved 2026-07-29). The platform-admin gate that stood here — one
 * `useExploreAccess` hook read by this page, the sidebar badge and the Dashboard deep
 * links — is gone; there is no access rule for Explore any more.
 */
export default function WasteExplorePage() {
  const { locale, t } = useTranslation();
  // Single-site (like the Dashboard): default to the first site alphabetically; the
  // filter state (site/sensor/…) rides useWasteFilters' URL params, shared with the
  // reused FilterPopover. Explore's own controls live in useExploreState.
  const sitesQuery = useSitesWithActiveMenus();
  const defaultSiteId = useMemo(() => pickDefaultSiteId(sitesQuery.data ?? []), [sitesQuery.data]);
  const { filters } = useWasteFilters(EXPLORE_PRESET, defaultSiteId ?? null);
  const effectiveSiteId = filters.site_id ?? defaultSiteId;
  const devicesQuery = useDevices();
  // All kitchens (org-wide) → annotate each device with its site_id, so the popover can
  // scope the sensor list to the site being chosen (draft), live, before Apply.
  const allKitchensQuery = useKitchens(null);
  const devicesWithSite = useMemo(() => {
    const kitchenSite = new Map((allKitchensQuery.data ?? []).map((k) => [k.id, k.site_id]));
    return (devicesQuery.data ?? []).map((d) => ({
      ...d,
      site_id: kitchenSite.get(d.kitchen_id) ?? null,
    }));
  }, [devicesQuery.data, allKitchensQuery.data]);

  const siteName = useMemo(
    () => (sitesQuery.data ?? []).find((s) => s.id === effectiveSiteId)?.name ?? null,
    [sitesQuery.data, effectiveSiteId],
  );

  // Currency follows the org→site→kitchen config stack, scoped to the active filter
  // (same resolution as the Dashboard / per-event cost — never hardcoded).
  const { orgId: claimsOrgId, isPlatformAdmin } = useClaims();
  const adminOrgId = useAdminOrgStore((s) => s.orgId);
  const orgId = isPlatformAdmin ? adminOrgId : claimsOrgId;
  const configEntity: { type: EntityType; id: string | null } = filters.kitchen_id
    ? { type: "kitchen", id: filters.kitchen_id }
    : effectiveSiteId
      ? { type: "site", id: effectiveSiteId }
      : { type: "organisation", id: orgId || null };
  const resolvedConfig = useResolvedConfig(configEntity.type, configEntity.id).data?.config;
  const resolvedCurrency = resolvedConfig?.currency?.value;
  const currency = typeof resolvedCurrency === "string" ? resolvedCurrency : "GBP";
  // Same resolution stack as the currency; the RPC buckets in this zone, so the views
  // densify in it too. Org-level tz is NOT NULL server-side, so this always resolves.
  const resolvedTimezone = resolvedConfig?.timezone?.value;
  const timezone = typeof resolvedTimezone === "string" ? resolvedTimezone : "UTC";

  // Window/controls live in the URL. `now` defaults to mount time; the site `timezone` anchors
  // the window to the RPC's site-local buckets (POS2-1036), so this reads after tz resolves.
  const { state, setGran, setMeasure, setView } = useExploreState(undefined, timezone);

  // The window as inclusive civil days, for the capture-feed link (POS2-886). `state.window.to`
  // is the day's last millisecond for a custom range but the next day's midnight for a default
  // window, so step back a millisecond — the same `lastDay` convention useExploreState uses.
  const windowDays = useMemo(
    () => ({
      from: zonedDay(state.window.from, timezone),
      to: zonedDay(new Date(state.window.to.getTime() - 1), timezone),
    }),
    [state.window, timezone],
  );

  // The engine (POS2-882): pivot for the current window + a sequenced prior-window
  // query for the per-measure delta. Measure switches never refetch (measure is
  // client-side); the shaped pivot is what 883/884 will render.
  const explore = useExploreData(state, filters, effectiveSiteId ?? null);
  const pivot = explore.data ? shapeExplorePivot(explore.data) : null;
  // Preceding-window pivot (already fetched for the panel delta) — the Table uses its last bucket
  // as the first row's Δ baseline (POS2-1038).
  const priorPivot = explore.priorData ? shapeExplorePivot(explore.priorData) : null;
  const measureDef = EXPLORE_MEASURES[state.measure];
  const totalValue = pivot ? measureDef.accessor(pivot.grandTotal) : null;
  const deltaValue = explore.delta[state.measure];
  const isEmpty = pivot != null && pivot.recordCount === 0 && !explore.isLoading;

  const title = exploreTitle(state.measure, state.gran, state.split, { currency, locale, t });

  // The Line view draws a Bar when the window holds one bucket (POS2-1050). The note that says
  // so sits beside the title rather than above the chart, so appearing and disappearing never
  // shifts the chart down (Artem). Only while a chart is actually on screen: the engine keeps the
  // previous pivot as placeholder data, so `pivot` outlives an error or a too-large window.
  const lineAsBar =
    pivot != null &&
    !isEmpty &&
    !explore.isError &&
    !explore.isTooLarge &&
    state.view === "line" &&
    isSingleBucketWindow(pivot, state.window, state.gran, timezone);

  // Active-filter caption for the exports (Artem) — which slice of data the chart represents. Site
  // is always the scope; the rest appear only when set. Baked into the PNG + printed under the PDF
  // chart (the date window is already in the header, the split in the title).
  const filterRows = [
    siteName ? { label: t("waste.site"), value: siteName } : null,
    filters.device_serial ? { label: t("waste.sensor"), value: filters.device_serial } : null,
    filters.waste_type
      ? { label: t("waste.wasteType"), value: wasteTypeLabel(t, filters.waste_type) }
      : null,
    filters.service_time
      ? { label: t("waste.fieldService"), value: serviceTimeLabel(t, filters.service_time) }
      : null,
    filters.time_from || filters.time_to
      ? {
          label: t("waste.filterRowTime"),
          value: `${filters.time_from ?? ""}–${filters.time_to ?? ""}`,
        }
      : null,
  ].filter((r): r is { label: string; value: string } => r !== null);

  // Measure as a segmented toggle like the Dashboard lens (POS2-851): currency / kg / captures.
  const measureOptions: { value: Measure; label: ReactNode }[] = [
    { value: "gbp", label: currencySymbol(locale, currency) },
    { value: "kg", label: "kg" },
    { value: "caps", label: <Camera className="size-3.5" aria-hidden /> },
  ];

  // Filters caption (Site/Sensor/…) — print-only, says which slice of data this is. It sits at the
  // TOP for the Table view (not buried after a long table) and under the chart for the other views.
  const filterCaption = filterRows.length > 0 && (
    <table className="mt-3 hidden text-xs print:table">
      <tbody>
        {filterRows.map((f) => (
          <tr key={f.label}>
            <td className="pr-4 align-top font-medium text-gray-400">{f.label}</td>
            <td className="text-gray-700">{f.value}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );

  return (
    <div className="flex h-full min-h-0 flex-col">
      {/* Topbar: granularity + measure toggles; date range + Split live inside the Filters popover.
          Hidden on print/PDF (POS2-885) — the chart panel is the WYSIWYG export. */}
      <div className="flex flex-wrap items-center gap-3 border-b border-gray-100 px-6 py-3 print:hidden">
        <h1 className="flex min-w-0 items-baseline gap-1.5 font-display text-xl font-extrabold tracking-tight text-gray-900">
          <span className="shrink-0">{t("waste.exploreTitle")}</span>
          {siteName && (
            <>
              <span className="shrink-0 text-gray-300">·</span>
              {/* Long site names truncate with the full name on hover, so the topbar
                  controls never get pushed off / wrapped. */}
              <span className="max-w-[16rem] truncate" title={siteName}>
                {siteName}
              </span>
            </>
          )}
        </h1>
        <div className="flex-1" />
        <SegmentedToggle
          value={state.gran}
          options={GRAN_OPTION_KEYS.map((o) => ({ value: o.value, label: t(o.labelKey) }))}
          onChange={setGran}
          ariaLabel={t("waste.granularityToggle")}
        />
        <SegmentedToggle
          ariaLabel={t("waste.measureToggle")}
          value={state.measure}
          options={measureOptions}
          onChange={setMeasure}
        />
        <FilterPopover
          sites={sitesQuery.data ?? []}
          kitchens={[]}
          devices={devicesWithSite}
          defaultPreset={EXPLORE_PRESET}
          defaultSiteId={defaultSiteId ?? null}
          singleSite
          fields={EXPLORE_FILTER_FIELDS}
          split={{ value: state.split }}
          datesDefault={state.defaultRange}
        />
        <ExportMenu
          pivot={pivot}
          measure={state.measure}
          gran={state.gran}
          view={state.view}
          currency={currency}
          title={title}
          windowText={state.windowText}
          total={totalValue != null ? measureDef.format(totalValue, { currency, locale, t }) : null}
          isTooLarge={explore.isTooLarge}
        />
      </div>

      {/* Full-height canvas. On print/PDF the padding + panel border drop so it fills the page. */}
      <div className="flex min-h-0 flex-1 flex-col p-6 print:p-0">
        <div className="flex min-h-0 flex-1 flex-col rounded-lg border border-black/[0.06] bg-white p-4 print:rounded-none print:border-0 print:p-0">
          {/* Panel chrome */}
          <div className="mb-3 flex flex-wrap items-center justify-between gap-3">
            <div>
              <div className="flex flex-wrap items-baseline gap-2">
                <span className="text-sm font-medium text-gray-900">{title}</span>
                {lineAsBar && (
                  <span className="text-xs text-gray-400" data-testid="explore-line-as-bar-note">
                    {t("waste.singleBucketNote", { unit: t(GRAN_UNIT_KEYS[state.gran]) })}
                  </span>
                )}
              </div>
              <div className="text-xs text-gray-400">{state.windowText}</div>
            </div>
            <div className="flex items-center gap-3">
              {/* Total + delta (engine, POS2-882). Delta hidden when null / <0.5%. */}
              <span
                className={cn(
                  "flex items-baseline gap-2 text-sm font-medium tabular-nums",
                  totalValue != null ? "text-gray-900" : "text-gray-400",
                )}
                data-testid="explore-total"
              >
                {totalValue != null ? measureDef.format(totalValue, { currency, locale, t }) : "—"}
                {deltaValue != null && (
                  <span
                    className={cn(
                      "text-xs font-semibold",
                      // Waste going up is the bad direction.
                      deltaValue > 0 ? "text-red-600" : "text-emerald-600",
                    )}
                    title={t("waste.vsPreceding")}
                    data-testid="explore-delta"
                  >
                    {deltaValue > 0 ? "▲" : "▼"} {formatPercent(locale, Math.abs(deltaValue))}
                  </span>
                )}
              </span>
              {/* View switcher — an interactive control, hidden in print/PDF (POS2-885). */}
              <div className="print:hidden">
                <SegmentedToggle
                  value={state.view}
                  options={VIEW_OPTION_KEYS.map((o) => {
                    // Pie needs a split dimension — greyed out (with a hint) until one is chosen.
                    const pieNeedsSplit = o.value === "pie" && state.split === "none";
                    return {
                      value: o.value,
                      label: t(o.labelKey),
                      disabled: pieNeedsSplit,
                      title: pieNeedsSplit ? t("waste.pieNeedsSplit") : undefined,
                    };
                  })}
                  onChange={(v) => setView(v as ExploreView)}
                  ariaLabel={t("waste.viewToggle")}
                />
              </div>
            </div>
          </div>

          {/* Table view: filters caption at the top, before the (long) table. */}
          {state.view === "table" && filterCaption}

          {/* The engine (POS2-882) surfaces the load states; Line/Bar render over the
              pivot (POS2-883). Pie/Table still land on the placeholder (POS2-884). */}
          {explore.isTooLarge ? (
            <CanvasMessage>{t("waste.windowTooLarge")}</CanvasMessage>
          ) : explore.isError ? (
            <CanvasMessage>{t("waste.loadViewFailed")}</CanvasMessage>
          ) : isEmpty ? (
            <CanvasMessage>{t("waste.noRecords")}</CanvasMessage>
          ) : pivot == null ? (
            <Skeleton className="min-h-0 w-full flex-1 rounded-md" />
          ) : state.view === "line" ? (
            <ExploreLine
              pivot={pivot}
              priorPivot={priorPivot}
              measure={state.measure}
              currency={currency}
              gran={state.gran}
              split={state.split}
              window={state.window}
              timezone={timezone}
            />
          ) : state.view === "bar" ? (
            <ExploreBar
              pivot={pivot}
              priorPivot={priorPivot}
              measure={state.measure}
              currency={currency}
              gran={state.gran}
              split={state.split}
              window={state.window}
              timezone={timezone}
            />
          ) : state.view === "pie" ? (
            <ExplorePie
              pivot={pivot}
              priorPivot={priorPivot}
              measure={state.measure}
              currency={currency}
              gran={state.gran}
              split={state.split}
              window={state.window}
              timezone={timezone}
            />
          ) : (
            <ExploreTable
              pivot={pivot}
              priorPivot={priorPivot}
              measure={state.measure}
              currency={currency}
              gran={state.gran}
              split={state.split}
              window={state.window}
              timezone={timezone}
            />
          )}

          {/* Chart views: filters caption below the chart. */}
          {state.view !== "table" && filterCaption}

          {/* Footer: record count (engine) + provenance chip. The count links back to the
              proof layer carrying this exact window and scope (POS2-886) — the loop from a
              finding to the captures behind it. */}
          <div className="mt-3 flex flex-wrap items-center justify-between gap-2 border-t border-gray-100 pt-3 text-xs text-gray-400">
            {pivot != null ? (
              <Link
                to={transactionsHref({
                  window: { from: windowDays.from, to: windowDays.to },
                  filters: {
                    site_id: effectiveSiteId,
                    kitchen_id: filters.kitchen_id,
                    device_serial: filters.device_serial,
                    waste_type: filters.waste_type,
                    service_time: filters.service_time,
                    food: filters.food,
                    time_from: filters.time_from,
                    time_to: filters.time_to,
                  },
                })}
                className="underline decoration-dotted underline-offset-2 transition-colors hover:text-gray-600"
                data-testid="explore-footer"
              >
                {t("waste.recordsBehind", { count: formatNumber(locale, pivot.recordCount) })}
              </Link>
            ) : (
              <span data-testid="explore-footer">{t("waste.recordsBehindSuffix")}</span>
            )}
            <span className="rounded-full bg-emerald-50 px-2 py-0.5 text-[10px] font-medium uppercase tracking-wide text-emerald-700">
              {t("waste.measuredBadge")}
            </span>
          </div>
        </div>
      </div>
    </div>
  );
}

// WYSIWYG exports (POS2-885): what's on the canvas is what you export — CSV pivot, PNG of
// the live chart SVG, and PDF via the browser's print dialog (print CSS strips the chrome).
function ExportMenu({
  pivot,
  measure,
  gran,
  view,
  currency,
  title,
  windowText,
  total,
  isTooLarge,
}: {
  pivot: ExplorePivot | null;
  measure: Measure;
  gran: Granularity;
  view: ExploreView;
  currency: string;
  title: string;
  windowText: string;
  total: string | null;
  isTooLarge: boolean;
}) {
  const { locale, t } = useTranslation();
  const [open, setOpen] = useState(false);

  // Nothing renders a chart/pivot until the window has records — guard all three exports the same
  // way (loading → pivot null; empty window → recordCount 0) so none produces an empty artefact.
  // A too-large window has data but the RPC 503'd (isTooLarge) — say so rather than "no data".
  const hasData = pivot != null && pivot.recordCount > 0;
  const noDataMsg = isTooLarge ? t("waste.windowTooLargeToExport") : t("waste.noDataToExport");

  function exportCsv() {
    setOpen(false);
    if (!pivot || !hasData) return void toast.error(noDataMsg);
    const blob = new Blob([exploreCsv(pivot, measure, gran, { currency, locale, t })], {
      type: "text/csv;charset=utf-8",
    });
    const url = URL.createObjectURL(blob);
    downloadHref(url, exportFilename(measure, gran, "csv"));
    URL.revokeObjectURL(url);
    toast.success(t("waste.csvDownloaded"));
  }

  function exportPng() {
    setOpen(false);
    if (view === "table") return void toast(t("waste.tableExportsAsCsv"));
    if (!pivot || !hasData) return void toast.error(noDataMsg);
    // The rendered view owns the svg (a single-bucket Line falls back to a Bar), so match any
    // explore-view chart rather than keying the selector to `view`.
    const svg = document.querySelector<SVGSVGElement>('[data-testid^="explore-view-"] svg');
    if (!svg) return void toast.error(noDataMsg);

    // Serialise a clone with computed fills inlined so the axis-tick text (coloured via a CSS
    // variable on the wrapper, not an attribute) survives; series/marks are literal hex already.
    const clone = svg.cloneNode(true) as SVGSVGElement;
    clone.setAttribute("xmlns", "http://www.w3.org/2000/svg");
    const cloneText = clone.querySelectorAll("text");
    svg.querySelectorAll("text").forEach((t, i) => {
      cloneText[i]?.setAttribute("fill", getComputedStyle(t).fill);
    });

    // The legend (colour → split label, + value/% on the pie) is HTML beside the chart, so it is
    // NOT in the serialised svg. Rebuild it onto the canvas so a split export says what's what —
    // matching each view's on-screen legend (pie: value + %; line/bar: label only).
    const fmt = (n: number) => EXPLORE_MEASURES[measure].format(n, { currency, locale, t });
    const legend: { color: string; label: string; value: string | null; share: number | null }[] =
      view === "pie"
        ? buildPieData(pivot, measure).slices.map((s) => ({
            color: s.color,
            label: s.label,
            value: fmt(s.value),
            share: s.share,
          }))
        : buildChartData(pivot, measure).series.map((s) => ({
            color: s.color,
            label: s.label,
            value: null,
            share: null,
          }));
    const showLegend = legend.length > 1;

    const box = svg.viewBox.baseVal;
    const w = box?.width || svg.clientWidth;
    const h = box?.height || svg.clientHeight;
    const scale = w < 700 ? 3 : 2; // 3× for narrow viewBoxes so the raster stays crisp (design §7)
    const headerH = 52; // white band above the chart carrying the panel title / window / total
    const rowH = 20;
    const legendH = showLegend ? legend.length * rowH + 12 : 0;
    // The PNG is a clean chart image — header + chart + filters caption + legend. The per-bucket
    // value table + Δ lives only in the PDF (paginated) and the CSV (Artem): a full-year daily
    // table baked here was an unreadable, giant image.
    const font = "Inter, system-ui, -apple-system, sans-serif";

    const img = new Image();
    img.onload = () => {
      const canvas = document.createElement("canvas");
      canvas.width = w * scale;
      canvas.height = (headerH + h + legendH) * scale;
      const ctx = canvas.getContext("2d");
      if (!ctx) return void toast.error(t("waste.exportFailedShort"));
      ctx.scale(scale, scale); // work in CSS px; the transform keeps the raster crisp
      ctx.fillStyle = "#ffffff";
      ctx.fillRect(0, 0, w, headerH + h + legendH);

      // Header — mirror the on-screen panel chrome (title + window left, total right) so the PNG
      // is self-describing in a GM pack / client email, not a context-free line (POS2-885 WYSIWYG).
      ctx.fillStyle = "#111827";
      ctx.font = `600 15px ${font}`;
      ctx.fillText(title, 4, 20);
      ctx.fillStyle = "#6b7280";
      ctx.font = `400 12px ${font}`;
      ctx.fillText(windowText, 4, 38);
      if (total) {
        ctx.fillStyle = "#111827";
        ctx.font = `600 15px ${font}`;
        ctx.textAlign = "right";
        ctx.fillText(total, w - 4, 24);
        ctx.textAlign = "left";
      }

      ctx.drawImage(img, 0, headerH, w, h);

      // Legend band below the chart — swatch + split label (+ value/% on the pie). The filters
      // context (site / sensor / waste type / …) is PDF-only, not baked into the PNG (Artem).
      if (showLegend) {
        const legendTop = headerH + h + 6;
        ctx.textBaseline = "middle";
        legend.forEach((item, i) => {
          const cy = legendTop + i * rowH + rowH / 2;
          ctx.fillStyle = item.color;
          ctx.fillRect(6, cy - 5, 10, 10);
          ctx.fillStyle = "#374151";
          ctx.font = `500 12px ${font}`;
          ctx.textAlign = "left";
          const rightReserve = item.value ? 96 : 8;
          ctx.fillText(fitText(ctx, item.label, w - 24 - rightReserve), 22, cy);
          if (item.value) {
            ctx.fillStyle = "#111827";
            ctx.font = `600 12px ${font}`;
            ctx.textAlign = "right";
            ctx.fillText(item.value, w - 44, cy);
            if (item.share != null) {
              ctx.fillStyle = "#9ca3af";
              ctx.font = `500 12px ${font}`;
              ctx.fillText(`${Math.round(item.share * 100)}%`, w - 6, cy);
            }
          }
        });
        ctx.textBaseline = "alphabetic";
        ctx.textAlign = "left";
      }

      downloadHref(canvas.toDataURL("image/png"), exportFilename(measure, gran, "png"));
      toast.success(t("waste.pngDownloaded"));
    };
    img.onerror = () => void toast.error(t("waste.exportFailedShort"));
    img.src = `data:image/svg+xml;charset=utf-8,${encodeURIComponent(new XMLSerializer().serializeToString(clone))}`;
  }

  function exportPdf() {
    setOpen(false);
    if (!hasData) return void toast.error(noDataMsg);
    window.print();
  }

  const actions: { label: string; run: () => void }[] = [
    { label: "PDF", run: exportPdf },
    { label: "PNG", run: exportPng },
    { label: "CSV", run: exportCsv },
  ];

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          size="sm"
          className="h-8 w-8 p-0"
          aria-label={t("waste.export")}
          data-testid="explore-export"
        >
          <Download className="size-4" />
        </Button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-40 p-1">
        {actions.map((a) => (
          <button
            key={a.label}
            type="button"
            className="w-full rounded-md px-3 py-1.5 text-left text-sm text-gray-700 hover:bg-gray-100"
            onClick={a.run}
          >
            {a.label}
          </button>
        ))}
      </PopoverContent>
    </Popover>
  );
}

/** Click a hidden anchor to save `href` as `filename` (the app's standard download idiom). */
function downloadHref(href: string, filename: string) {
  const a = document.createElement("a");
  a.href = href;
  a.download = filename;
  a.click();
}

/** Truncate `text` with an ellipsis to fit `maxWidth` under the canvas's current font. */
function fitText(ctx: CanvasRenderingContext2D, text: string, maxWidth: number): string {
  if (ctx.measureText(text).width <= maxWidth) return text;
  let t = text;
  while (t.length > 1 && ctx.measureText(`${t}…`).width > maxWidth) t = t.slice(0, -1);
  return `${t}…`;
}

// A centred canvas message for the engine's load states (too-large / error /
// empty), sharing the placeholder's frame so the panel doesn't jump.
function CanvasMessage({ children }: { children: ReactNode }) {
  return (
    <div
      className="flex min-h-0 flex-1 items-center justify-center rounded-md bg-gray-50/60"
      data-testid="explore-canvas-message"
    >
      <p className="text-xs text-gray-400">{children}</p>
    </div>
  );
}
